import * as tslib_1 from "tslib";
import { Matrix3D, PerspectiveProjection, Transform } from "@awayjs/core";
import { Image2D, Single2DTexture } from "@awayjs/graphics";
import { ShadowMapperBase } from "./ShadowMapperBase";
var DirectionalShadowMapper = (function (_super) {
    tslib_1.__extends(DirectionalShadowMapper, _super);
    function DirectionalShadowMapper() {
        var _this = _super.call(this) || this;
        _this._lightOffset = 10000;
        _this._snap = 64;
        _this._cullPlanes = [];
        _this._overallDepthProjection = new PerspectiveProjection();
        _this._overallDepthProjection.transform = new Transform();
        _this._localFrustum = [];
        _this._matrix = new Matrix3D();
        return _this;
    }
    Object.defineProperty(DirectionalShadowMapper.prototype, "snap", {
        get: function () {
            return this._snap;
        },
        set: function (value) {
            this._snap = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DirectionalShadowMapper.prototype, "lightOffset", {
        get: function () {
            return this._lightOffset;
        },
        set: function (value) {
            this._lightOffset = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DirectionalShadowMapper.prototype, "iDepthProjection", {
        //@arcane
        get: function () {
            return this._overallDepthProjection.viewMatrix3D;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DirectionalShadowMapper.prototype, "depth", {
        //@arcane
        get: function () {
            return this._maxZ - this._minZ;
        },
        enumerable: true,
        configurable: true
    });
    DirectionalShadowMapper.prototype.iSetDepthMap = function (depthMap) {
        if (this._depthMap == depthMap)
            return;
        _super.prototype.iSetDepthMap.call(this, depthMap);
        if (this._depthMap) {
            this._explicitDepthMap = true;
            this._depthMapSize = depthMap.image2D.rect.width;
        }
        else {
            this._explicitDepthMap = false;
        }
    };
    DirectionalShadowMapper.prototype._createDepthTexture = function () {
        return new Single2DTexture(new Image2D(this._depthMapSize, this._depthMapSize));
    };
    //@override
    DirectionalShadowMapper.prototype._drawDepthMap = function (view, target, renderer) {
        renderer.cullPlanes = this._cullPlanes;
        renderer._iRender(this._overallDepthProjection, view, target.image2D);
    };
    /**
     *
     * @param projection
     * @private
     */
    DirectionalShadowMapper.prototype._updateCullPlanes = function (projection) {
        var lightFrustumPlanes = this._overallDepthProjection.frustumPlanes;
        var viewFrustumPlanes = projection.frustumPlanes;
        this._cullPlanes.length = 4;
        this._cullPlanes[0] = lightFrustumPlanes[0];
        this._cullPlanes[1] = lightFrustumPlanes[1];
        this._cullPlanes[2] = lightFrustumPlanes[2];
        this._cullPlanes[3] = lightFrustumPlanes[3];
        var light = this._light;
        var dir = light.sceneDirection;
        var dirX = dir.x;
        var dirY = dir.y;
        var dirZ = dir.z;
        var j = 4;
        for (var i = 0; i < 6; ++i) {
            var plane = viewFrustumPlanes[i];
            if (plane.a * dirX + plane.b * dirY + plane.c * dirZ < 0)
                this._cullPlanes[j++] = plane;
        }
    };
    /**
     *
     * @param projection
     * @private
     */
    DirectionalShadowMapper.prototype._updateDepthProjection = function (projection) {
        this._updateProjectionFromFrustumCorners(projection, projection.frustumCorners, this._matrix);
        this._overallDepthProjection.frustumMatrix3D = this._matrix;
        this._updateCullPlanes(projection);
    };
    /**
     *
     * @param projection
     * @param matrix
     * @private
     */
    DirectionalShadowMapper.prototype._updateProjectionFromFrustumCorners = function (projection, corners, matrix) {
        var dir;
        var x, y, z;
        var minX, minY;
        var maxX, maxY;
        var i;
        var position = projection.transform.concatenatedMatrix3D.position;
        var light = this._light;
        dir = light.sceneDirection;
        this._overallDepthProjection.transform.matrix3D = this._light.transform.concatenatedMatrix3D;
        x = Math.floor((position.x - dir.x * this._lightOffset) / this._snap) * this._snap;
        y = Math.floor((position.y - dir.y * this._lightOffset) / this._snap) * this._snap;
        z = Math.floor((position.z - dir.z * this._lightOffset) / this._snap) * this._snap;
        this._overallDepthProjection.transform.moveTo(x, y, z);
        this._matrix.copyFrom(this._overallDepthProjection.transform.inverseConcatenatedMatrix3D);
        this._matrix.prepend(projection.transform.concatenatedMatrix3D);
        this._matrix.transformVectors(corners, this._localFrustum);
        minX = maxX = this._localFrustum[0];
        minY = maxY = this._localFrustum[1];
        this._maxZ = this._localFrustum[2];
        i = 3;
        while (i < 24) {
            x = this._localFrustum[i];
            y = this._localFrustum[i + 1];
            z = this._localFrustum[i + 2];
            if (x < minX)
                minX = x;
            if (x > maxX)
                maxX = x;
            if (y < minY)
                minY = y;
            if (y > maxY)
                maxY = y;
            if (z > this._maxZ)
                this._maxZ = z;
            i += 3;
        }
        this._minZ = 1;
        var w = maxX - minX;
        var h = maxY - minY;
        var d = 1 / (this._maxZ - this._minZ);
        if (minX < 0)
            minX -= this._snap; // because int() rounds up for < 0
        if (minY < 0)
            minY -= this._snap;
        minX = Math.floor(minX / this._snap) * this._snap;
        minY = Math.floor(minY / this._snap) * this._snap;
        var snap2 = 2 * this._snap;
        w = Math.floor(w / snap2 + 2) * snap2;
        h = Math.floor(h / snap2 + 2) * snap2;
        maxX = minX + w;
        maxY = minY + h;
        w = 1 / w;
        h = 1 / h;
        var raw = matrix._rawData;
        raw[0] = 2 * w;
        raw[5] = 2 * h;
        raw[10] = d;
        raw[12] = -(maxX + minX) * w;
        raw[13] = -(maxY + minY) * h;
        raw[14] = -this._minZ * d;
        raw[15] = 1;
        raw[1] = raw[2] = raw[3] = raw[4] = raw[6] = raw[7] = raw[8] = raw[9] = raw[11] = 0;
        matrix.invalidatePosition();
    };
    return DirectionalShadowMapper;
}(ShadowMapperBase));
export { DirectionalShadowMapper };



// WEBPACK FOOTER //
// ../dist/lib/shadowmappers/DirectionalShadowMapper.js